import { useMemo, useState } from 'react';
import {
  ArrowLeft,
  Bug,
  CheckCircle2,
  Copy,
  Mail,
  MessageSquareText,
  RefreshCw,
} from 'lucide-react';
import { isRouteErrorResponse, useRouteError } from 'react-router-dom';
import {
  getErrorMessage,
  isDynamicImportFailure,
  resetBrowserStateAndReload,
} from '@/app/utils/browser-recovery';

const SUPPORT_EMAIL = 'info@aqlengineering.com';

function getErrorDetails(error: unknown) {
  if (isRouteErrorResponse(error)) {
    return {
      title: error.status === 404 ? 'Página não encontrada' : 'Não foi possível abrir esta página',
      message:
        typeof error.data === 'string'
          ? error.data
          : error.statusText || 'O AQL Vision encontrou um erro de navegação.',
      technical: `RouteError ${error.status} ${error.statusText}\n${JSON.stringify(error.data ?? {}, null, 2)}`,
    };
  }

  if (error instanceof Error) {
    return {
      title: 'Algo não correu como esperado',
      message:
        'A aplicação encontrou um problema inesperado. Os seus dados guardados não foram alterados.',
      technical: `${error.name}: ${error.message}\n${error.stack || 'Sem stack trace'}`,
    };
  }

  return {
    title: 'Algo não correu como esperado',
    message: 'Encontrámos um problema inesperado ao carregar o AQL Vision.',
    technical: getErrorMessage(error),
  };
}

function getCurrentUser() {
  try {
    return (
      localStorage.getItem('aql_user_email') ||
      localStorage.getItem('userEmail') ||
      'não identificado'
    );
  } catch {
    return 'não identificado';
  }
}

export function RouteErrorBoundary() {
  const error = useRouteError();
  const details = useMemo(() => getErrorDetails(error), [error]);
  const moduleLoadFailure = isDynamicImportFailure(error);
  const [comment, setComment] = useState('');
  const [copied, setCopied] = useState(false);
  const [isRecovering, setIsRecovering] = useState(false);

  const report = useMemo(
    () =>
      [
        'Relatório de erro AQL Vision',
        '',
        `Página: ${window.location.href}`,
        `Data: ${new Date().toLocaleString('pt-PT')}`,
        `Navegador: ${navigator.userAgent}`,
        `Utilizador: ${getCurrentUser()}`,
        '',
        `Contexto operacional: ${comment.trim() || 'Sem comentário'}`,
        '',
        'Detalhes técnicos:',
        details.technical,
      ].join('\n'),
    [comment, details.technical],
  );

  const handleRecovery = async () => {
    setIsRecovering(true);

    try {
      if (moduleLoadFailure) {
        await resetBrowserStateAndReload();
        return;
      }

      window.location.reload();
    } catch (recoveryError) {
      console.error('Failed to recover from route error:', recoveryError);
      window.location.reload();
    }
  };

  const sendReport = () => {
    const subject = encodeURIComponent(`[AQL Vision] ${details.title}`);
    const body = encodeURIComponent(report);
    window.location.href = `mailto:${SUPPORT_EMAIL}?subject=${subject}&body=${body}`;
  };

  const copyReport = async () => {
    await navigator.clipboard.writeText(report);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1800);
  };

  const goBack = () => {
    if (window.history.length > 1) {
      window.history.back();
      return;
    }

    window.location.assign('/');
  };

  return (
    <main className="min-h-dvh bg-[#eef4f7] px-4 py-8 text-slate-900 sm:px-6 sm:py-12">
      <div className="mx-auto grid min-h-[calc(100dvh-6rem)] max-w-5xl overflow-hidden rounded-[32px] border border-white bg-white shadow-[0_30px_90px_rgba(15,23,42,0.12)] lg:grid-cols-[0.9fr_1.1fr]">
        <section className="relative flex flex-col justify-between overflow-hidden bg-[#071329] p-7 text-white sm:p-10">
          <div className="pointer-events-none absolute -left-20 -top-20 h-72 w-72 rounded-full bg-cyan-400/15 blur-3xl" />

          <div className="relative">
            <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-cyan-400 text-[#071329] shadow-[0_0_30px_rgba(34,211,238,0.28)]">
              <Bug className="h-7 w-7" strokeWidth={1.7} aria-hidden="true" />
            </div>
            <p className="mt-8 text-xs font-semibold uppercase tracking-[0.18em] text-cyan-300">
              AQL Support
            </p>
            <h1 className="mt-3 max-w-sm text-3xl font-bold tracking-tight sm:text-4xl">
              {details.title}
            </h1>
            <p className="mt-4 max-w-md text-sm leading-6 text-slate-300 sm:text-base">
              {details.message}
            </p>
          </div>

          <div className="relative mt-12 border-t border-white/10 pt-6 text-xs leading-5 text-slate-400">
            Pode tentar novamente ou enviar-nos o relatório. Incluímos automaticamente a página,
            data e informação técnica necessária para investigar.
          </div>
        </section>

        <section className="flex flex-col justify-center p-6 sm:p-10 lg:p-12">
          <div className="flex items-center gap-3">
            <MessageSquareText
              className="h-5 w-5 flex-none text-cyan-600"
              strokeWidth={1.7}
              aria-hidden="true"
            />
            <h2 className="text-xl font-semibold tracking-tight">
              Ajude-nos a perceber o que aconteceu
            </h2>
          </div>

          <label htmlFor="error-feedback" className="mt-7 text-sm font-medium text-slate-700">
            O que estava a fazer?
          </label>
          <textarea
            id="error-feedback"
            value={comment}
            onChange={(event) => setComment(event.target.value)}
            rows={5}
            placeholder="Ex.: estava a validar uma deteção quando o erro apareceu…"
            className="mt-2 w-full resize-none rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm leading-6 outline-none transition focus:border-cyan-400 focus:bg-white focus:ring-4 focus:ring-cyan-100"
          />

          <button
            type="button"
            onClick={sendReport}
            className="mt-5 flex h-12 w-full items-center justify-center gap-2 rounded-full bg-[#263370] px-5 text-sm font-semibold text-white shadow-lg shadow-[#263370]/20 transition hover:-translate-y-0.5 hover:bg-[#1d285c] focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-indigo-200"
          >
            <Mail className="h-5 w-5" strokeWidth={1.7} aria-hidden="true" />
            Enviar relatório para a AQL
          </button>
          <p className="mt-2 text-center text-xs text-slate-400">
            Abre o seu programa de email com o relatório preparado para {SUPPORT_EMAIL}.
          </p>

          <div className="mt-7 grid gap-3 sm:grid-cols-3">
            <button
              type="button"
              onClick={handleRecovery}
              disabled={isRecovering}
              className="flex h-11 items-center justify-center gap-2 rounded-xl border border-slate-200 text-sm font-medium text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <RefreshCw
                className={`h-4 w-4 ${isRecovering ? 'animate-spin' : ''}`}
                aria-hidden="true"
              />
              {isRecovering ? 'A recuperar…' : 'Tentar novamente'}
            </button>
            <button
              type="button"
              onClick={goBack}
              className="flex h-11 items-center justify-center gap-2 rounded-xl border border-slate-200 text-sm font-medium text-slate-700 transition hover:bg-slate-50"
            >
              <ArrowLeft className="h-4 w-4" aria-hidden="true" />
              Voltar
            </button>
            <button
              type="button"
              onClick={copyReport}
              className="flex h-11 items-center justify-center gap-2 rounded-xl border border-slate-200 text-sm font-medium text-slate-700 transition hover:bg-slate-50"
            >
              {copied ? (
                <CheckCircle2 className="h-4 w-4 text-emerald-600" aria-hidden="true" />
              ) : (
                <Copy className="h-4 w-4" aria-hidden="true" />
              )}
              {copied ? 'Copiado' : 'Copiar relatório'}
            </button>
          </div>

          <details className="mt-7 rounded-2xl border border-slate-200 bg-slate-50 p-4">
            <summary className="cursor-pointer text-sm font-medium text-slate-600">
              Ver detalhes técnicos
            </summary>
            <pre className="mt-4 max-h-40 overflow-auto whitespace-pre-wrap break-words rounded-xl bg-slate-950 p-4 text-[11px] leading-5 text-slate-300">
              {details.technical}
            </pre>
          </details>
        </section>
      </div>
    </main>
  );
}
